/**
=========================================================
* Soft UI Dashboard PRO React - v4.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/soft-ui-dashboard-pro-react
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// @mui material components
import Collapse from "@mui/material/Collapse";
import Icon from "@mui/material/Icon";

// Soft UI Dashboard PRO React components
import SoftBox from "components/SoftComponents/SoftBox";
import SoftTypography from "components/SoftComponents/SoftTypography";
import { Link, matchPath, useLocation } from "react-router-dom";
import { useRef } from "react";

function BaseNavbarLink({
  name,
  routes = false,
  openHandler = false,
  closeHandler = false,
  children = false,
  collapseStatus = false,
  appearance,
  isSelected = false,
  deviceLayout,
  ...rest
}) {

  const linkRef = useRef(null);
  const { pathname } = useLocation();
  const has_dropdown = routes.length > 0 && typeof routes[0].collapse != 'undefined' && routes[0]?.collapse?.filter(i => i.in_menu)?.length > 0;
  const subtext = routes?.[0]?.collapse?.find(r => {
    return matchPath({ path: r.route }, pathname)
  })?.name || false;

  return has_dropdown ? (
    <>
      <SoftBox
        {...rest}
        mx={{ xxs: 0.5, xxl: 1 }}
        py={subtext ? 0.5 : 1}
        px={{ xxs: 1, xxl: 2, x3l: 3 }}
        component={Link}
        to={routes[0].route}
        ref={linkRef}
        onMouseEnter={!children ? () => { if (openHandler) openHandler(linkRef.current) } : undefined}
        onMouseLeave={!children ? closeHandler : undefined}
        display="flex"
        alignItems="center"
        sx={({ palette, functions: { rgba } }) => ({
          cursor: "pointer",
          userSelect: "none",
          textDecoration: "none",
          borderRadius: deviceLayout != 'mobile' ? '20px' : '8px',
          color: isSelected ? rgba(palette[appearance].main, 1) : rgba(palette[appearance].text, 1),
          backgroundColor: isSelected ? rgba(palette[appearance].text, 1) : rgba(palette[appearance].main, 1),
          '&:hover': {
            color: rgba(palette[appearance].main, 1),
            backgroundColor: rgba(palette[appearance].text, 0.7),
          }
        })}
      >
        <SoftBox display="flex" flexDirection="column" color="inherit">
          <SoftBox display="flex" justifyContent="center" color="inherit">
            <SoftTypography
              variant="body2"
              fontWeight="regular"
              textTransform="capitalize"
              color="inherit"
              sx={{ fontWeight: "100%", lineHeight: 1.3 }}
            >
              {name.replace("_", " ")}
            </SoftTypography>
            <SoftTypography variant="body2" color="inherit" sx={{ lineHeight: 1.3 }}>
              <Icon sx={{ fontWeight: "bold", verticalAlign: "middle" }}>keyboard_arrow_down</Icon>
            </SoftTypography>
          </SoftBox>
          <SoftTypography color="inherit" textAlign={{xxs: 'left', md: 'center'}} sx={{ fontSize: '11px', lineHeight: 0.9, fontWeight: 'light' }}>
            {subtext}
          </SoftTypography>
        </SoftBox>
      </SoftBox>
      <Collapse in={Boolean(collapseStatus)} timeout="auto" unmountOnExit>
        {children}
      </Collapse>
    </>
  ) : (
    <SoftBox
      {...rest}
      mx={{ xxs: 0.5, xxl: 1 }}
      py={subtext ? 0.5 : 1}
      px={{ xxs: 1, xxl: 2, x3l: 3 }}
      component={Link}
      to={routes[0]?.route}
      display="flex"
      alignItems="center"
      sx={({ palette, functions: { rgba } }) => ({
        cursor: "pointer",
        userSelect: "none",
        textDecoration: "none",
        borderRadius: deviceLayout == 'mobile' ? '8px' : '20px',
        color: isSelected ? rgba(palette[appearance].main, 1) : rgba(palette[appearance].text, 1),
        backgroundColor: isSelected ? rgba(palette[appearance].text, 1) : rgba(palette[appearance].main, 1),
        '&:hover': {
          color: rgba(palette[appearance].main, 1),
          backgroundColor: rgba(palette[appearance].text, 0.7),
        }
      })}
    >
      <SoftBox display="flex" flexDirection="column" color="inherit">
        <SoftBox display="flex" justifyContent="center" color="inherit">
          <SoftTypography
            variant="body2"
            fontWeight="regular"
            textTransform="capitalize"
            color="inherit"
            sx={{ fontWeight: "100%", lineHeight: 1.3 }}
          >
            {name.replace("_", " ")}
          </SoftTypography>
        </SoftBox>
        <SoftTypography color="inherit" textAlign={{xxs: 'left', md: 'center'}} sx={{ fontSize: '11px', lineHeight: 0.9, fontWeight: 'light' }}>
          {subtext}
        </SoftTypography>
      </SoftBox>
    </SoftBox>
  );
}

// Typechecking props for the BaseNavbarLink
BaseNavbarLink.propTypes = {
  name: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
  openHandler: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  closeHandler: PropTypes.oneOfType([PropTypes.func, PropTypes.bool]),
  children: PropTypes.node,
  collapseStatus: PropTypes.bool,
  appearance: PropTypes.string,
  deviceLayout: PropTypes.string,
};

export default BaseNavbarLink;
